import { type FC, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspaceStore } from '@/stores/workspace.store';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

export const DashboardPage: FC = () => {
  const { workspaces, isLoading, fetchWorkspaces, createWorkspace } = useWorkspaceStore();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await createWorkspace({ name: name.trim(), description: description.trim() || undefined });
    setName('');
    setDescription('');
    setShowForm(false);
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--spacing-xl)',
        }}
      >
        <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>Workspaces</h1>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : 'New workspace'}
        </Button>
      </div>

      {showForm && (
        <form
          onSubmit={handleCreate}
          style={{
            padding: 'var(--spacing-lg)',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            marginBottom: 'var(--spacing-lg)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-md)',
          }}
        >
          <Input
            label="Workspace name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Product Strategy 2026"
            required
          />
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
          />
          <Button type="submit" style={{ alignSelf: 'flex-start' }}>
            Create workspace
          </Button>
        </form>
      )}

      {isLoading && (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading workspaces...</p>
      )}

      {!isLoading && workspaces.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: 'var(--spacing-2xl)',
            color: 'var(--color-text-muted)',
          }}
        >
          <p style={{ fontSize: 'var(--font-size-lg)', marginBottom: 'var(--spacing-sm)' }}>
            No workspaces yet
          </p>
          <p>Create your first workspace to get started with strategic mapping.</p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
        {workspaces.map((workspace) => (
          <Link
            key={workspace.id}
            to={`/workspaces/${workspace.id}`}
            style={{
              display: 'block',
              padding: 'var(--spacing-md) var(--spacing-lg)',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              textDecoration: 'none',
              transition: 'var(--transition-fast)',
            }}
          >
            <h3
              style={{
                fontSize: 'var(--font-size-md)',
                fontWeight: 600,
                color: 'var(--color-text)',
                marginBottom: 'var(--spacing-xs)',
              }}
            >
              {workspace.name}
            </h3>
            {workspace.description && (
              <p
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-muted)',
                }}
              >
                {workspace.description}
              </p>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default DashboardPage;
